import { Application, ControllerConstructor } from '@hotwired/stimulus';
import { controllerPathToStimulusControllerName } from '@/common/utilities';
import FocusController from '@/controllers/focus_controller';
import NavburgerController from '@/controllers/navburger_controller';

const BASE_PATH = '/controllers';
const application = Application.start();

// Eagerly registered: only layout-level controllers with zero heavy dependencies.
// datadog_controller and flow_events_controller are intentionally lazy — both
// transitively import the Datadog SDK (~170 KB), so eager-loading either one
// pulls that cost into the synchronous bundle on every page.
application.register('focus', FocusController);
application.register('navburger', NavburgerController);

// All remaining controllers are loaded on demand when their data-controller
// attribute appears in the DOM. Stimulus ignores unknown identifiers until
// registration, so elements are unenhanced briefly but never flash.
const lazyControllers = import.meta.glob(
  [
    '@/controllers/**/*_controller.*',
    '!@/controllers/focus_controller.*',
    '!@/controllers/navburger_controller.*',
  ],
  { import: 'default', eager: false },
);

const loaderByIdentifier = new Map<string, () => Promise<ControllerConstructor>>();
for (const path in lazyControllers) {
  loaderByIdentifier.set(
    controllerPathToStimulusControllerName(path, BASE_PATH),
    lazyControllers[path] as () => Promise<ControllerConstructor>,
  );
}

const loading = new Set<string>();

function loadController(identifier: string) {
  if (loading.has(identifier) || !loaderByIdentifier.has(identifier)) {
    return;
  }
  loading.add(identifier);
  loaderByIdentifier.get(identifier)!()
    .then((cls) => application.register(identifier, cls))
    .catch(() => {
      loading.delete(identifier);
    });
}

function identifiersFromElement(el: Element): string[] {
  return (el.getAttribute('data-controller') ?? '').trim().split(/\s+/).filter(Boolean);
}

// type="module" scripts are deferred, so the DOM is fully parsed at this point.
document
  .querySelectorAll('[data-controller]')
  .forEach((el) => identifiersFromElement(el).forEach(loadController));

// Handle controllers added dynamically via Turbo frame updates or other DOM mutations.
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.type === 'attributes') {
      identifiersFromElement(mutation.target as Element).forEach(loadController);
    }
    for (const node of mutation.addedNodes) {
      if (node instanceof Element) {
        identifiersFromElement(node).forEach(loadController);
        node
          .querySelectorAll('[data-controller]')
          .forEach((el) => identifiersFromElement(el).forEach(loadController));
      }
    }
  }
}).observe(document.documentElement, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['data-controller'],
});
